/**
 * Install-Fähigkeit des Browsers — eine Quelle der Wahrheit (#680).
 *
 * Chromium kündigt eine mögliche Installation über `beforeinstallprompt` an;
 * iOS Safari kennt das Event nicht und braucht eine Anleitung. Desktop-Firefox
 * und Desktop-Safari können überhaupt nicht installieren — dort darf gar kein
 * Angebot erscheinen, sonst führt der Haupt-Button ins Leere.
 *
 * Der Listener hängt bewusst auf Modulebene statt in einer Komponente: das Event
 * feuert früh, oft bevor React montiert ist. Wer es erst beim Öffnen der Sheet
 * abonniert, verpasst es — und genau daraus entstand die Henne-Ei-Situation, in
 * der die Sheet sich zeigen musste, um überhaupt zu erfahren, ob sie darf.
 */

let deferredPrompt = null;
const listeners = new Set();

function handleBeforeInstallPrompt(event) {
  // Verhindert Chromiums eigenen Installations-Balken; wir fragen im eigenen
  // Sheet und lösen das Event später über prompt() aus.
  event.preventDefault();
  deferredPrompt = event;
  listeners.forEach((listener) => listener());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
}

export const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS 13+ meldet sich als Macintosh — über Touchpunkte erkennbar
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

export const isIOSSafari = () => {
  if (!isIOS()) return false;
  const ua = navigator.userAgent || '';
  // Andere iOS-Browser setzen eine eigene Kennung; Safari hat keine davon
  return !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/.test(ua);
};

/**
 * Kann dieser Browser die App überhaupt installieren?
 *
 * Entweder liegt ein abgefangenes `beforeinstallprompt` vor (Chromium), oder es
 * ist ein iOS-Gerät — dort geht es manuell über das Teilen-Menü, wofür die Sheet
 * eine Anleitung zeigt. iOS-Fremdbrowser sind bewusst eingeschlossen: sie
 * bekommen in der Anleitung den ehrlichen Hinweis, dass es nur in Safari geht.
 */
export const canInstall = () => deferredPrompt !== null || isIOS();

/** Das abgefangene Event, oder null. */
export const getDeferredPrompt = () => deferredPrompt;

/** Nach Gebrauch (oder im Test) verwerfen — ein Event ist nur einmal nutzbar. */
export const clearDeferredPrompt = () => {
  deferredPrompt = null;
};

/**
 * Meldet, sobald eine Installation möglich wird. Gibt die Abmeldefunktion
 * zurück. Nötig, weil Chromium das Event asynchron und teils erst nach dem
 * Mounten feuert — ohne Benachrichtigung bliebe das Angebot bis zum nächsten
 * Seitenaufruf aus.
 */
export const subscribeToInstallAvailability = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
